/**
 * Loumars Theme (Swiss Editorial) — Standalone Drafting Studio Custom Element
 * ===========================================================================
 * Provides framework-agnostic <loumars-drafting-studio> Web Component for
 * headless embedding across external design systems, CAD portals, and preview studios.
 *
 * Implements Sculpted Hardware design system invariants:
 *  - Drafting Canvas Atmosphere: Pure white (#FFFFFF) with 24px CAD grid
 *  - Pitch Obsidian Monolith: Pitch dark chassis (#0A0A0C) with 1px border (#26262B)
 *  - Dual-Atmosphere Contrast: Crimson Signal (#D90429) on light canvas, Rose-400 (#FB7185) on obsidian
 *  - 4-Tier Curvature Hierarchy: Sharp (2px), Technical (6px), Tactile (12px), Pill (9999px)
 */

export type DraftingScaleRatio = '1:100' | '1:50' | '1:20' | '1:10';
export type DraftingViewport = 'desktop' | 'tablet' | 'mobile' | 'ultrawide';
export type DraftingAtmosphere = 'light' | 'obsidian';
export type DraftingCurvature = 'sharp' | 'technical' | 'tactile' | 'pill';

export interface DraftingStudioPreset {
  id: DraftingViewport;
  name: string;
  width: number;
  height: number;
  aspectRatio: string;
  description: string;
}

export const CANONICAL_DRAFTING_VIEWPORTS: Record<DraftingViewport, DraftingStudioPreset> = {
  desktop: {
    id: 'desktop',
    name: 'Workstation Canvas',
    width: 1280,
    height: 800,
    aspectRatio: '16:10',
    description: 'Precision CAD drafting workstation with dual coordinate monitors.',
  },
  tablet: {
    id: 'tablet',
    name: 'Tactile Slate',
    width: 768,
    height: 1024,
    aspectRatio: '3:4',
    description: 'Field engineer tablet display with high-density blueprint rendering.',
  },
  mobile: {
    id: 'mobile',
    name: 'Pocket Schematics',
    width: 375,
    height: 667,
    aspectRatio: '9:16',
    description: 'Handheld technician viewport with compact telemetry gauges.',
  },
  ultrawide: {
    id: 'ultrawide',
    name: 'Master Command Studio',
    width: 1920,
    height: 1080,
    aspectRatio: '16:9',
    description: 'Full-spectrum architectural console with multi-layer overlays.',
  },
};

export const CANONICAL_SCALE_FACTORS: Record<DraftingScaleRatio, number> = {
  '1:100': 100,
  '1:50': 50,
  '1:20': 20,
  '1:10': 10,
};

export interface DraftingMetricsResult {
  viewport: DraftingViewport;
  viewportName: string;
  width: number;
  height: number;
  scale: DraftingScaleRatio;
  atmosphere: DraftingAtmosphere;
  curvature: DraftingCurvature;
  gridEnabled: boolean;
  gridCellCount: number;
  scaledAreaM2: number;
  contrastRatio: number;
  contrastGrade: 'AAA' | 'AA';
  primaryAccent: string;
  curvaturePx: number;
}

export function calculateDraftingMetrics(
  viewport: DraftingViewport = 'desktop',
  scale: DraftingScaleRatio = '1:50',
  atmosphere: DraftingAtmosphere = 'light',
  curvature: DraftingCurvature = 'technical',
  gridEnabled: boolean = true
): DraftingMetricsResult {
  const vp = CANONICAL_DRAFTING_VIEWPORTS[viewport] || CANONICAL_DRAFTING_VIEWPORTS.desktop;
  const factor = CANONICAL_SCALE_FACTORS[scale] || 50;

  const width = vp.width;
  const height = vp.height;
  const gridCols = Math.floor(width / 24);
  const gridRows = Math.floor(height / 24);
  const gridCellCount = gridCols * gridRows;

  // Real-world area: (width mm * factor) * (height mm * factor) / 1,000,000 m2
  const scaledAreaM2 = Number(((width * factor * (height * factor)) / 1_000_000).toFixed(1));

  const isLight = atmosphere === 'light';
  const contrastRatio = isLight ? 18.0 : 19.5;
  const primaryAccent = isLight ? '#D90429' : '#FB7185';

  const curvatureMap: Record<DraftingCurvature, number> = {
    sharp: 2,
    technical: 6,
    tactile: 12,
    pill: 9999,
  };
  const curvaturePx = curvatureMap[curvature] ?? 6;

  return {
    viewport: vp.id,
    viewportName: vp.name,
    width,
    height,
    scale,
    atmosphere,
    curvature,
    gridEnabled,
    gridCellCount,
    scaledAreaM2,
    contrastRatio,
    contrastGrade: 'AAA',
    primaryAccent,
    curvaturePx,
  };
}

export class LoumarsDraftingStudioElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['viewport', 'scale', 'atmosphere', 'curvature', 'grid'];
  }

  private _viewport: DraftingViewport = 'desktop';
  private _scale: DraftingScaleRatio = '1:50';
  private _atmosphere: DraftingAtmosphere = 'light';
  private _curvature: DraftingCurvature = 'technical';
  private _grid: boolean = true;
  private _isMounted: boolean = false;

  get viewport(): DraftingViewport {
    return this._viewport;
  }
  set viewport(val: DraftingViewport) {
    if (CANONICAL_DRAFTING_VIEWPORTS[val]) {
      this._viewport = val;
      this.setAttribute('viewport', val);
      this.updateAndDispatch();
    }
  }

  get scale(): DraftingScaleRatio {
    return this._scale;
  }
  set scale(val: DraftingScaleRatio) {
    if (CANONICAL_SCALE_FACTORS[val]) {
      this._scale = val;
      this.setAttribute('scale', val);
      this.updateAndDispatch();
    }
  }

  get atmosphere(): DraftingAtmosphere {
    return this._atmosphere;
  }
  set atmosphere(val: DraftingAtmosphere) {
    if (val === 'light' || val === 'obsidian') {
      this._atmosphere = val;
      this.setAttribute('atmosphere', val);
      this.updateAndDispatch();
    }
  }

  get curvature(): DraftingCurvature {
    return this._curvature;
  }
  set curvature(val: DraftingCurvature) {
    if (val === 'sharp' || val === 'technical' || val === 'tactile' || val === 'pill') {
      this._curvature = val;
      this.setAttribute('curvature', val);
      this.updateAndDispatch();
    }
  }

  get grid(): boolean {
    return this._grid;
  }
  set grid(val: boolean) {
    this._grid = Boolean(val);
    this.setAttribute('grid', String(this._grid));
    this.updateAndDispatch();
  }

  setViewport(vp: DraftingViewport): void {
    this.viewport = vp;
  }

  setScale(s: DraftingScaleRatio): void {
    this.scale = s;
  }

  setAtmosphere(a: DraftingAtmosphere): void {
    this.atmosphere = a;
  }

  setCurvature(c: DraftingCurvature): void {
    this.curvature = c;
  }

  setGrid(g: boolean): void {
    this.grid = g;
  }

  getMetrics(): DraftingMetricsResult {
    return calculateDraftingMetrics(
      this._viewport,
      this._scale,
      this._atmosphere,
      this._curvature,
      this._grid
    );
  }

  connectedCallback(): void {
    this._isMounted = true;
    this.readAttributes();
    this.render();
    this.bindEvents();
    this.dispatchDraftingChange();
  }

  disconnectedCallback(): void {
    this._isMounted = false;
  }

  attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null): void {
    if (!this._isMounted || oldVal === newVal) return;
    this.readAttributes();
    this.render();
    this.bindEvents();
    this.dispatchDraftingChange();
  }

  private readAttributes(): void {
    const vp = this.getAttribute('viewport') as DraftingViewport | null;
    if (vp && CANONICAL_DRAFTING_VIEWPORTS[vp]) {
      this._viewport = vp;
    }

    const sc = this.getAttribute('scale') as DraftingScaleRatio | null;
    if (sc && CANONICAL_SCALE_FACTORS[sc]) {
      this._scale = sc;
    }

    const at = this.getAttribute('atmosphere') as DraftingAtmosphere | null;
    if (at === 'light' || at === 'obsidian') {
      this._atmosphere = at;
    }

    const cu = this.getAttribute('curvature') as DraftingCurvature | null;
    if (cu === 'sharp' || cu === 'technical' || cu === 'tactile' || cu === 'pill') {
      this._curvature = cu;
    }

    const gr = this.getAttribute('grid');
    if (gr !== null) {
      this._grid = gr !== 'false';
    }
  }

  private updateAndDispatch(): void {
    if (this._isMounted) {
      this.render();
      this.bindEvents();
      this.dispatchDraftingChange();
    }
  }

  private dispatchDraftingChange(): void {
    const metrics = this.getMetrics();
    this.dispatchEvent(
      new CustomEvent('drafting-change', {
        bubbles: true,
        composed: true,
        detail: {
          viewport: this._viewport,
          scale: this._scale,
          atmosphere: this._atmosphere,
          curvature: this._curvature,
          grid: this._grid,
          metrics,
        },
      })
    );
  }

  private render(): void {
    const metrics = this.getMetrics();
    const isLight = this._atmosphere === 'light';

    const bgClass = isLight ? 'bg-white text-slate-900 border-slate-200' : 'bg-[#0A0A0C] text-slate-100 border-[#26262B]';
    const panelBg = isLight ? 'bg-slate-50 border-slate-200' : 'bg-[#141418] border-[#26262B]';
    const textMuted = isLight ? 'text-slate-600' : 'text-slate-400';
    const accentColor = metrics.primaryAccent;

    const gridBackground = this._grid
      ? isLight
        ? 'background-image: radial-gradient(circle, rgba(0,0,0,0.12) 1px, transparent 1px); background-size: 24px 24px;'
        : 'background-image: radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px); background-size: 24px 24px;'
      : '';

    const radiusStyle = metrics.curvature === 'pill' ? '9999px' : `${metrics.curvaturePx}px`;

    this.innerHTML = `
      <div class="loumars-drafting-studio border shadow-xl p-6 font-sans transition-colors duration-200 ${bgClass}" style="border-radius: ${radiusStyle};">
        <!-- Header Console Bar -->
        <div class="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-inherit mb-6">
          <div class="flex items-center gap-3">
            <span class="w-3 h-3 rounded-full" style="background-color: ${accentColor};"></span>
            <div>
              <div class="text-xs font-mono font-bold tracking-wider uppercase opacity-75">┼ SYS.COORD: CH-8001 / ZURICH</div>
              <h2 class="text-lg font-bold tracking-tight">SWISS EDITORIAL DRAFTING STUDIO // SCULPTED HARDWARE</h2>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <span class="px-2.5 py-1 text-xs font-mono font-medium rounded-full border border-inherit ${panelBg}">
              WCAG 2.1 ${metrics.contrastGrade} // ${metrics.contrastRatio}:1
            </span>
            <button type="button" data-drafting-toggle-atmosphere class="px-3 py-1 text-xs font-bold font-mono uppercase rounded border border-inherit hover:opacity-80 transition cursor-pointer ${panelBg}">
              Mode: ${this._atmosphere.toUpperCase()}
            </button>
          </div>
        </div>

        <!-- Controls Toolbar -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 text-sm">
          <!-- Viewport Presets -->
          <div>
            <label class="block text-xs font-mono uppercase mb-1.5 opacity-75">Viewport Preset</label>
            <div class="flex flex-wrap gap-1">
              ${(['desktop', 'tablet', 'mobile', 'ultrawide'] as DraftingViewport[])
                .map(
                  (vp) => `
                <button type="button" data-drafting-viewport="${vp}" class="px-2 py-1 text-xs font-mono rounded border border-inherit ${
                    this._viewport === vp ? 'font-bold' : 'opacity-70'
                  } ${this._viewport === vp ? (isLight ? 'bg-slate-900 text-white' : 'bg-white text-slate-900') : panelBg}">
                  ${vp.toUpperCase()}
                </button>
              `
                )
                .join('')}
            </div>
          </div>

          <!-- Scale Ratio -->
          <div>
            <label class="block text-xs font-mono uppercase mb-1.5 opacity-75">Scale Ratio</label>
            <div class="flex flex-wrap gap-1">
              ${(['1:100', '1:50', '1:20', '1:10'] as DraftingScaleRatio[])
                .map(
                  (sc) => `
                <button type="button" data-drafting-scale="${sc}" class="px-2 py-1 text-xs font-mono rounded border border-inherit ${
                    this._scale === sc ? 'font-bold' : 'opacity-70'
                  } ${this._scale === sc ? (isLight ? 'bg-slate-900 text-white' : 'bg-white text-slate-900') : panelBg}">
                  ${sc}
                </button>
              `
                )
                .join('')}
            </div>
          </div>

          <!-- Curvature Hierarchy -->
          <div>
            <label class="block text-xs font-mono uppercase mb-1.5 opacity-75">Curvature</label>
            <div class="flex flex-wrap gap-1">
              ${(['sharp', 'technical', 'tactile', 'pill'] as DraftingCurvature[])
                .map(
                  (cu) => `
                <button type="button" data-drafting-curvature="${cu}" class="px-2 py-1 text-xs font-mono rounded border border-inherit ${
                    this._curvature === cu ? 'font-bold' : 'opacity-70'
                  } ${this._curvature === cu ? (isLight ? 'bg-slate-900 text-white' : 'bg-white text-slate-900') : panelBg}">
                  ${cu.toUpperCase()}
                </button>
              `
                )
                .join('')}
            </div>
          </div>

          <!-- CAD Grid Switch -->
          <div>
            <label class="block text-xs font-mono uppercase mb-1.5 opacity-75">CAD Grid Overlay</label>
            <label class="inline-flex items-center gap-2 cursor-pointer pt-1">
              <input type="checkbox" data-drafting-grid ${this._grid ? 'checked' : ''} class="w-4 h-4 rounded border-inherit">
              <span class="text-xs font-mono">24px Coordinate Grid</span>
            </label>
          </div>
        </div>

        <!-- Canvas Preview Area -->
        <div class="relative border border-inherit overflow-hidden p-6 mb-6 transition-all duration-300 min-h-[220px] flex flex-col justify-between" style="border-radius: ${radiusStyle}; ${gridBackground}">
          <!-- Coordinate Markers -->
          <div class="flex justify-between text-[11px] font-mono opacity-60">
            <span>┼ (0, 0)</span>
            <span>┼ (${metrics.width}, 0)</span>
          </div>

          <!-- Central Spec Readout -->
          <div class="my-auto text-center py-4">
            <div class="inline-block px-4 py-2 border border-inherit shadow-sm ${panelBg}" style="border-radius: ${radiusStyle};">
              <div class="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">${metrics.viewportName}</div>
              <div class="text-2xl font-extrabold tracking-tight font-mono">${metrics.width} × ${metrics.height} px</div>
              <div class="text-xs mt-1 ${textMuted}">Scaled Real-World Footprint: <span class="font-bold">${metrics.scaledAreaM2} m²</span></div>
            </div>
          </div>

          <div class="flex justify-between text-[11px] font-mono opacity-60">
            <span>┼ (0, ${metrics.height})</span>
            <span>┼ (${metrics.width}, ${metrics.height})</span>
          </div>
        </div>

        <!-- Metrics Footer Bar -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
          <div class="p-2.5 border border-inherit rounded ${panelBg}">
            <div class="opacity-60 text-[10px] uppercase">CAD Grid Cells</div>
            <div class="text-base font-bold">${metrics.gridCellCount} cells</div>
          </div>
          <div class="p-2.5 border border-inherit rounded ${panelBg}">
            <div class="opacity-60 text-[10px] uppercase">Scale Ratio</div>
            <div class="text-base font-bold">${metrics.scale}</div>
          </div>
          <div class="p-2.5 border border-inherit rounded ${panelBg}">
            <div class="opacity-60 text-[10px] uppercase">Curvature Radius</div>
            <div class="text-base font-bold">${metrics.curvaturePx} px</div>
          </div>
          <div class="p-2.5 border border-inherit rounded ${panelBg}">
            <div class="opacity-60 text-[10px] uppercase">Signal Accent</div>
            <div class="flex items-center gap-1.5 text-base font-bold">
              <span class="w-3 h-3 rounded-full" style="background-color: ${accentColor};"></span>
              <span>${accentColor}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private bindEvents(): void {
    const atmosphereBtn = this.querySelector('[data-drafting-toggle-atmosphere]');
    if (atmosphereBtn) {
      atmosphereBtn.addEventListener('click', () => {
        this.atmosphere = this._atmosphere === 'light' ? 'obsidian' : 'light';
      });
    }

    const vpButtons = this.querySelectorAll<HTMLButtonElement>('[data-drafting-viewport]');
    vpButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const vp = btn.getAttribute('data-drafting-viewport') as DraftingViewport;
        if (vp) this.viewport = vp;
      });
    });

    const scaleButtons = this.querySelectorAll<HTMLButtonElement>('[data-drafting-scale]');
    scaleButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const sc = btn.getAttribute('data-drafting-scale') as DraftingScaleRatio;
        if (sc) this.scale = sc;
      });
    });

    const curvatureButtons = this.querySelectorAll<HTMLButtonElement>('[data-drafting-curvature]');
    curvatureButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const cu = btn.getAttribute('data-drafting-curvature') as DraftingCurvature;
        if (cu) this.curvature = cu;
      });
    });

    const gridCheckbox = this.querySelector<HTMLInputElement>('[data-drafting-grid]');
    if (gridCheckbox) {
      gridCheckbox.addEventListener('change', () => {
        this.grid = gridCheckbox.checked;
      });
    }
  }
}

export function registerDraftingStudioElement(): void {
  if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
    if (!customElements.get('loumars-drafting-studio')) {
      customElements.define('loumars-drafting-studio', LoumarsDraftingStudioElement);
    }
  }
}

// Auto-register on script load
if (typeof window !== 'undefined') {
  registerDraftingStudioElement();
}
